import { useState } from 'react'
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Tooltip as RechartsTooltip,
	ReferenceLine,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from 'recharts'
import { Text } from '../Text'
import { DISTRIBUTION_TIERS } from './shared'
import { useChartCursor } from './useChartCursor'

export interface RoleScore {
	roleName: string
	avgRating: number
	highestRating: number
	lowestRating: number
}

const DEAL_OVERALL_COLOR = '#3B82F6'
const HIGHEST_COLOR = '#10B981'
const LOWEST_COLOR = '#EF4444'

const tierColor = (key: (typeof DISTRIBUTION_TIERS)[number]['key']) => {
	const tier = DISTRIBUTION_TIERS.find((t) => t.key === key)
	if (!tier) throw new Error(`Unknown distribution tier: ${key}`)
	return tier.color
}

// Cycle bars through tier colors so each role is visually distinct.
const ROLE_BAR_COLORS = [
	tierColor('good'),
	tierColor('needsImprovement'),
	tierColor('average'),
	tierColor('excellent'),
	tierColor('critical'),
	'#C4B5FD',
	'#99F6E4',
	'#FEF08A',
]

type RoleTooltipDatum = { roleName: string; avgRating: number; highestRating: number; lowestRating: number }
type RoleTooltipEntry = { payload: RoleTooltipDatum }

function ScoreByRoleTooltip({
	active,
	payload,
	overallRating,
}: Readonly<{
	active?: boolean
	payload?: RoleTooltipEntry[]
	overallRating: number | null
}>) {
	if (!active || !payload?.length) return null
	const d = payload[0].payload
	const vsOverall = overallRating === null ? null : Number((d.avgRating - overallRating).toFixed(2))
	const sign = (v: number) => (v > 0 ? '+' : '')
	const deltaClass = (v: number) => {
		if (v > 0) return 'text-emerald-600'
		if (v < 0) return 'text-red-600'
		return 'text-neutral-600'
	}
	return (
		<div className='rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-md text-sm flex flex-col gap-2 min-w-40'>
			<span className='font-medium text-neutral-900'>{d.roleName}</span>
			<div className='flex gap-4'>
				<div className='flex flex-col gap-0.5'>
					<span className='text-xs text-neutral-500'>Avg</span>
					<span className='font-semibold text-neutral-800'>{d.avgRating.toFixed(2)}</span>
				</div>
				<div className='flex flex-col gap-0.5'>
					<span className='text-xs text-neutral-500'>Highest</span>
					<span className='font-semibold text-emerald-600'>{d.highestRating.toFixed(2)}</span>
				</div>
				<div className='flex flex-col gap-0.5'>
					<span className='text-xs text-neutral-500'>Lowest</span>
					<span className='font-semibold text-red-600'>{d.lowestRating.toFixed(2)}</span>
				</div>
				{vsOverall !== null && (
					<div className='flex flex-col gap-0.5'>
						<span className='text-xs text-neutral-500'>vs. Overall</span>
						<span className={`font-semibold ${deltaClass(vsOverall)}`}>
							{sign(vsOverall)}
							{vsOverall.toFixed(2)}
						</span>
					</div>
				)}
			</div>
		</div>
	)
}

export function ScoreByRoleChart({
	roleScores,
	overallRating,
	scaleMax = 10,
}: Readonly<{
	roleScores: RoleScore[]
	overallRating: number | null
	scaleMax?: number
}>) {
	const { containerRef, cursorPos, onMouseMove, onMouseLeave: onContainerMouseLeave } = useChartCursor(220)
	const [activeIndex, setActiveIndex] = useState<number | null>(null)

	if (roleScores.length === 0) {
		return (
			<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
				<Text variant='medium' weight='semibold'>
					Score by Role
				</Text>
				<div className='flex h-48 items-center justify-center text-neutral-400 text-sm'>No role scores to display.</div>
			</div>
		)
	}

	const highestRanking = roleScores.reduce<number | null>(
		(acc, r) => (acc === null || r.highestRating > acc ? r.highestRating : acc),
		null,
	)
	const lowestRanking = roleScores.reduce<number | null>(
		(acc, r) => (acc === null || r.lowestRating < acc ? r.lowestRating : acc),
		null,
	)

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
			<Text variant='medium' weight='semibold'>
				Score by Role
			</Text>
			<div
				ref={containerRef}
				role='img'
				aria-label='Score by Role bar chart'
				style={{ height: Math.max(160, roleScores.length * 40 + 30) }}
				onMouseMove={onMouseMove}
				onMouseLeave={onContainerMouseLeave}>
				<ResponsiveContainer width='100%' height='100%'>
					<BarChart
						layout='vertical'
						data={roleScores.map((r) => ({
							roleName: r.roleName,
							avgRating: r.avgRating,
							highestRating: r.highestRating,
							lowestRating: r.lowestRating,
						}))}
						margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
						onMouseLeave={() => setActiveIndex(null)}>
						<CartesianGrid strokeDasharray='3 3' stroke='#F3F4F6' horizontal={false} />
						<XAxis
							type='number'
							domain={[0, scaleMax]}
							ticks={Array.from({ length: 6 }, (_, i) => (i * scaleMax) / 5)}
							tick={{ fontSize: 12, fill: '#9CA3AF' }}
							axisLine={false}
							tickLine={false}
						/>
						<YAxis
							type='category'
							dataKey='roleName'
							tick={{ fontSize: 12, fill: '#374151' }}
							axisLine={false}
							tickLine={false}
							width={100}
						/>
						<RechartsTooltip
							cursor={{ fill: '#F8FAFC' }}
							content={<ScoreByRoleTooltip overallRating={overallRating} />}
							position={cursorPos}
							isAnimationActive={false}
						/>
						<Bar
							dataKey='avgRating'
							radius={[0, 4, 4, 0]}
							onMouseEnter={(_, index) => setActiveIndex(index)}
							onMouseLeave={() => setActiveIndex(null)}>
							{roleScores.map((r, i) => (
								<Cell
									key={r.roleName}
									fill={ROLE_BAR_COLORS[i % ROLE_BAR_COLORS.length]}
									opacity={activeIndex !== null && activeIndex !== i ? 0.3 : 1}
								/>
							))}
						</Bar>
						{overallRating !== null && (
							<ReferenceLine x={overallRating} stroke={DEAL_OVERALL_COLOR} strokeWidth={2} ifOverflow='extendDomain' />
						)}
						{highestRanking !== null && (
							<ReferenceLine x={highestRanking} stroke={HIGHEST_COLOR} strokeWidth={2} ifOverflow='extendDomain' />
						)}
						{lowestRanking !== null && (
							<ReferenceLine x={lowestRanking} stroke={LOWEST_COLOR} strokeWidth={2} ifOverflow='extendDomain' />
						)}
					</BarChart>
				</ResponsiveContainer>
			</div>
			<div className='mt-auto flex items-center justify-center gap-6 text-xs text-neutral-600'>
				<div className='flex items-center gap-2'>
					<span className='inline-block h-0.5 w-4' style={{ backgroundColor: DEAL_OVERALL_COLOR }} />
					<span>Deal Overall</span>
				</div>
				<div className='flex items-center gap-2'>
					<span className='inline-block h-0.5 w-4' style={{ backgroundColor: HIGHEST_COLOR }} />
					<span>Highest Ranking</span>
				</div>
				<div className='flex items-center gap-2'>
					<span className='inline-block h-0.5 w-4' style={{ backgroundColor: LOWEST_COLOR }} />
					<span>Lowest Ranking</span>
				</div>
			</div>
		</div>
	)
}
